import { useCallback, useMemo, type SetStateAction } from "react";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import {
  featureFlagToggled,
  featureFlagsChanged,
  selectEffectiveFeatureFlags,
  selectFeatureFlagsLoaded,
  selectHasEarlyAccess,
  selectStoredFeatureFlags,
  type FeatureFlag
} from "../store/slices/featureFlagsSlice";
import { FLAGS_WITHOUT_EARLY_ACCESS } from "../utils/localStorage/featureFlagsStorage";

export type FeatureFlagContextValue = {
  featureFlags: FeatureFlag[];
  setFeatureFlags: (flags: SetStateAction<FeatureFlag[]>) => void;
  toggleFlag: (key: string, isEnabled: boolean) => void;
  isFlagEnabled: (key: string) => boolean;
  featureFlagsLoaded: boolean;
};

export const useFeatureFlagContext = (): FeatureFlagContextValue => {
  const dispatch = useAppDispatch();
  const featureFlags = useAppSelector(selectEffectiveFeatureFlags);
  const storedFeatureFlags = useAppSelector(selectStoredFeatureFlags);
  const hasEarlyAccess = useAppSelector(selectHasEarlyAccess);
  const featureFlagsLoaded = useAppSelector(selectFeatureFlagsLoaded);

  const setFeatureFlags = useCallback(
    (nextFlags: SetStateAction<FeatureFlag[]>) => {
      dispatch(
        featureFlagsChanged(
          typeof nextFlags === "function"
            ? nextFlags(storedFeatureFlags)
            : nextFlags
        )
      );
    },
    [dispatch, storedFeatureFlags]
  );
  const toggleFlag = useCallback(
    (key: string, isEnabled: boolean) => {
      if (!hasEarlyAccess && !FLAGS_WITHOUT_EARLY_ACCESS.includes(key)) {
        return;
      }
      dispatch(featureFlagToggled({ key, isEnabled }));
    },
    [dispatch, hasEarlyAccess]
  );
  const isFlagEnabled = useCallback(
    (key: string) =>
      !!featureFlags.find((flag) => flag.key === key && flag.isEnabled),
    [featureFlags]
  );

  return useMemo(
    () => ({
      featureFlags,
      setFeatureFlags,
      toggleFlag,
      isFlagEnabled,
      featureFlagsLoaded
    }),
    [
      featureFlags,
      setFeatureFlags,
      toggleFlag,
      isFlagEnabled,
      featureFlagsLoaded
    ]
  );
};
